import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl, fdatetime } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Lock, LockOpen, TrendingUp, TrendingDown, Wallet, Loader2 } from "lucide-react";

export default function Caixa() {
  const qc = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [openingAmount, setOpeningAmount] = useState("");
  const [openNotes, setOpenNotes] = useState("");
  const [counted, setCounted] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [openResp, setOpenResp] = useState("");
  const [closeResp, setCloseResp] = useState("");
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["cash"],
    queryFn: async () => (await api.get("/cash/current")).data,
    refetchInterval: 15000,
  });
  const { data: history } = useQuery({
    queryKey: ["cash-history"],
    queryFn: async () => (await api.get("/cash/history")).data,
  });
  const { data: sellers } = useQuery({
    queryKey: ["sellers"],
    queryFn: async () => (await api.get("/sellers")).data,
  });

  const session = data?.session;
  const totals = data?.totals;

  const openCash = async () => {
    setSaving(true);
    try {
      await api.post("/cash/open", {
        opening_amount: parseFloat(String(openingAmount).replace(",", ".")) || 0,
        notes: openNotes,
        responsible: openResp,
      });
      toast.success("Caixa aberto com sucesso");
      setOpenDialog(false);
      setOpeningAmount("");
      setOpenNotes("");
      setOpenResp("");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const closeCash = async () => {
    setSaving(true);
    try {
      const r = await api.post("/cash/close", {
        counted_cash: parseFloat(String(counted).replace(",", ".")) || 0,
        notes: closeNotes,
        responsible: closeResp,
      });
      const diff = r.data.closing.difference;
      if (Math.abs(diff) < 0.005) toast.success("Caixa fechado — conferência exata");
      else if (diff > 0) toast.warning(`Caixa fechado com sobra de ${brl(diff)}`);
      else toast.error(`Caixa fechado com falta de ${brl(Math.abs(diff))}`);
      setCloseDialog(false);
      setCounted("");
      setCloseNotes("");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const countedNum = parseFloat(String(counted).replace(",", ".")) || 0;
  const diff = totals ? countedNum - totals.expected_cash : 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="caixa-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Caixa</h1>
          <p className="text-sm text-muted-foreground">Abertura, acompanhamento e fechamento do dia</p>
        </div>
        {session ? (
          <Button
            data-testid="close-cash-btn"
            variant="outline"
            className="border-red-500/40 text-red-500 hover:bg-red-500/10"
            onClick={() => setCloseDialog(true)}
          >
            <Lock className="mr-2 h-4 w-4" /> Fechar caixa
          </Button>
        ) : (
          <Button
            data-testid="open-cash-btn"
            className="bg-emerald-600 text-white hover:bg-emerald-500"
            onClick={() => setOpenDialog(true)}
          >
            <LockOpen className="mr-2 h-4 w-4" /> Abrir caixa
          </Button>
        )}
      </div>

      {!session ? (
        <Card data-testid="cash-empty-state">
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-muted">
              <Lock className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold">Caixa fechado</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Abra o caixa informando o valor inicial para começar as vendas do dia.
              </p>
            </div>
            <Button
              data-testid="open-cash-empty-btn"
              className="bg-emerald-600 text-white hover:bg-emerald-500"
              onClick={() => setOpenDialog(true)}
            >
              <LockOpen className="mr-2 h-4 w-4" /> Abrir caixa agora
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-5 py-3 text-sm" data-testid="cash-open-info">
            <span className="font-medium text-emerald-500">Caixa aberto</span>
            <span className="text-muted-foreground">
              {" "}por {session.responsible || session.opened_by} às {fdatetime(session.opened_at)}
            </span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard testid="cash-opening" title="Saldo inicial" value={session.opening_amount} icon={Wallet} />
            <StatCard testid="cash-entradas" title="Entradas" value={totals.entradas} icon={TrendingUp} tone="success"
              sub={`${totals.sales_count} vendas`} />
            <StatCard testid="cash-saidas" title="Saídas" value={totals.saidas} icon={TrendingDown} tone="danger" />
            <StatCard testid="cash-expected" title="Esperado em dinheiro" value={totals.expected_cash} icon={Wallet} />
          </div>
          <div className="grid gap-4 grid-cols-2 xl:grid-cols-5">
            {Object.entries(totals.by_type).map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border bg-card px-4 py-3" data-testid={`cash-type-${k}`}>
                <div className="text-xs uppercase tracking-[0.1em] text-muted-foreground">{k}</div>
                <div className="num mt-1 font-semibold">{brl(v)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Histórico de caixas</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="cash-history-table">
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Responsável</TableHead>
                <TableHead className="text-right">Inicial</TableHead>
                <TableHead className="text-right">Entradas</TableHead>
                <TableHead className="text-right">Saídas</TableHead>
                <TableHead className="text-right">Esperado</TableHead>
                <TableHead className="text-right">Contado</TableHead>
                <TableHead className="text-right">Diferença</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(history || []).map((h) => (
                <TableRow key={h.id} data-testid={`cash-history-${h.id}`}>
                  <TableCell className="whitespace-nowrap">{fdatetime(h.closed_at)}</TableCell>
                  <TableCell>{h.closing?.responsible || h.closed_by}</TableCell>
                  <TableCell className="num text-right">{brl(h.opening_amount)}</TableCell>
                  <TableCell className="num text-right text-emerald-500">{brl(h.closing?.entradas)}</TableCell>
                  <TableCell className="num text-right text-red-500">{brl(h.closing?.saidas)}</TableCell>
                  <TableCell className="num text-right">{brl(h.closing?.expected_cash)}</TableCell>
                  <TableCell className="num text-right">{brl(h.closing?.counted_cash)}</TableCell>
                  <TableCell className="text-right">
                    <Badge
                      variant="outline"
                      className={
                        Math.abs(h.closing?.difference || 0) < 0.005
                          ? "border-emerald-500/40 text-emerald-500"
                          : "border-red-500/40 text-red-500"
                      }
                    >
                      {brl(h.closing?.difference || 0)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {(history || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    Nenhum caixa fechado ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-sm" data-testid="open-cash-dialog">
          <DialogHeader>
            <DialogTitle>Abrir caixa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Valor inicial em dinheiro (R$)</Label>
              <Input
                autoFocus
                data-testid="open-cash-amount"
                inputMode="decimal"
                placeholder="0,00"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                className="num h-11"
              />
            </div>
            <div className="space-y-2">
              <Label>Responsável pela abertura</Label>
              <Select value={openResp} onValueChange={setOpenResp}>
                <SelectTrigger data-testid="open-cash-responsible">
                  <SelectValue placeholder="Selecionar (padrão: usuário logado)" />
                </SelectTrigger>
                <SelectContent>
                  {(sellers || []).filter((s) => s.active).map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Textarea data-testid="open-cash-notes" value={openNotes} onChange={(e) => setOpenNotes(e.target.value)} rows={2} />
            </div>
            <Button
              data-testid="open-cash-confirm"
              onClick={openCash}
              disabled={saving}
              className="h-11 w-full bg-emerald-600 text-white hover:bg-emerald-500"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar abertura
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="max-w-md" data-testid="close-cash-dialog">
          <DialogHeader>
            <DialogTitle>Fechar caixa</DialogTitle>
          </DialogHeader>
          {totals && (
            <div className="space-y-4">
              <div className="rounded-xl border border-border divide-y divide-border text-sm">
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-muted-foreground">Saldo inicial</span>
                  <span className="num">{brl(session.opening_amount)}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-muted-foreground">Entradas</span>
                  <span className="num text-emerald-500">{brl(totals.entradas)}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-muted-foreground">Saídas</span>
                  <span className="num text-red-500">{brl(totals.saidas)}</span>
                </div>
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-muted-foreground">PIX / Débito / Crédito</span>
                  <span className="num">
                    {brl(totals.by_type.pix)} / {brl(totals.by_type.debito)} / {brl(totals.by_type.credito)}
                  </span>
                </div>
                <div className="flex justify-between px-4 py-2.5 font-semibold">
                  <span>Esperado em dinheiro</span>
                  <span className="num" data-testid="close-expected">{brl(totals.expected_cash)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Valor contado em dinheiro (R$)</Label>
                <Input
                  autoFocus
                  data-testid="close-counted-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={counted}
                  onChange={(e) => setCounted(e.target.value)}
                  className="num h-11"
                />
              </div>
              {counted !== "" && (
                <div
                  data-testid="close-difference"
                  className={`rounded-lg px-4 py-3 text-sm font-medium ${
                    Math.abs(diff) < 0.005
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-red-500/10 text-red-500"
                  }`}
                >
                  Diferença: {brl(diff)} {Math.abs(diff) < 0.005 ? "— conferência exata" : diff > 0 ? "(sobra)" : "(falta)"}
                </div>
              )}
              <div className="space-y-2">
                <Label>Responsável pelo fechamento</Label>
                <Select value={closeResp} onValueChange={setCloseResp}>
                  <SelectTrigger data-testid="close-cash-responsible">
                    <SelectValue placeholder="Selecionar (padrão: usuário logado)" />
                  </SelectTrigger>
                  <SelectContent>
                    {(sellers || []).filter((s) => s.active).map((s) => (
                      <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Observações (opcional)</Label>
                <Textarea data-testid="close-notes" value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} rows={2} />
              </div>
              <Button
                data-testid="close-cash-confirm"
                onClick={closeCash}
                disabled={saving || counted === ""}
                className="h-11 w-full"
                variant="destructive"
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar fechamento
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
